import { Challenge, ChallengeObj, ConstraintObj } from "@/interfaces/vidyaGrid";
import {
  COL_CHALLENGES,
  GENRES,
  PLATFORMS,
  ROW_CHALLENGES,
  THEMES,
} from "@/lib/constants";
import { pickConstraint } from "@/lib/utils";

class VidyaGridGame {
  public colChallenges: ChallengeObj[] = [];
  public rowChallenges: ChallengeObj[] = [];

  private availablePlatforms: ConstraintObj[] = structuredClone(PLATFORMS);
  private availableThemes: ConstraintObj[] = structuredClone(THEMES);
  private availableGenres: ConstraintObj[] = structuredClone(GENRES);

  private generation = 0;
  private yearRange = {
    start: 1990,
    end: 2024,
  };

  private challengeLiterals: Record<Challenge, () => ChallengeObj> = {
    platform: () => {
      this.availablePlatforms = this.availablePlatforms.filter(
        (c) =>
          c.fairStartYear! >= this.yearRange.start ||
          c.fairEndYear! <= this.yearRange.end
      );
      const constraint = pickConstraint(this.availablePlatforms);
      this.availablePlatforms = this.availablePlatforms.filter(
        (c) => c.id !== constraint.id
      );

      if (this.generation === 0) this.generation = constraint.generation!;
      this.availablePlatforms = this.availablePlatforms.filter(
        (c) => Math.abs(c.generation! - constraint.generation!) < 1
      );

      this.yearRange.start = Math.max(
        this.yearRange.start,
        constraint.fairStartYear ?? this.yearRange.start
      );
      this.yearRange.end = Math.min(
        this.yearRange.end,
        constraint.fairEndYear ?? this.yearRange.end
      );
      const yearConstraint = this.rowChallenges.find(
        (c) => c.type === "year_of_release"
      );
      if (yearConstraint) {
        const index = this.rowChallenges.indexOf(yearConstraint);
        this.rowChallenges[index] = this.challengeLiterals.year_of_release();
      }

      return {
        label: `Platform: ${constraint.name}`,
        type: "platform",
        constraint,
      };
    },
    theme: () => {
      const constraint = pickConstraint(this.availableThemes);
      this.availableThemes = this.availableThemes.filter(
        (c) => c.id !== constraint.id
      );
      return {
        label: `Theme: ${constraint.name}`,
        type: "theme",
        constraint,
      };
    },
    genre: () => {
      const constraint = pickConstraint(this.availableGenres);
      this.availableGenres = this.availableGenres.filter(
        (c) => c.id !== constraint.id
      );
      return {
        label: `Genre: ${constraint.name}`,
        type: "genre",
        constraint,
      };
    },
    year_of_release: () => {
      const constraint = Math.floor(
        Math.random() * (this.yearRange.end - this.yearRange.start + 1) +
          this.yearRange.start
      );
      return {
        label: `First release year: ${constraint}`,
        type: "year_of_release",
        constraint,
      };
    },
    rating: () => {
      const constraint = Math.floor(Math.random() * (75 - 30 + 1) + 30);
      return {
        label: `Rating higher than ${constraint}`,
        type: "rating",
        constraint,
        solved: false,
      };
    },
  };

  constructor() {
    this.rowChallenges = this.pickChallenges([
      ...structuredClone(ROW_CHALLENGES),
    ]);
    this.colChallenges = this.pickChallenges([
      ...structuredClone(COL_CHALLENGES),
    ]);
  }

  private pickChallenges(challenges: Challenge[]): ChallengeObj[] {
    const selectedChallenges: ChallengeObj[] = [];
    while (selectedChallenges.length < 3) {
      const num = Math.floor(Math.random() * challenges.length);
      const colChallenge = challenges.slice(num, num + 1)[0];
      selectedChallenges.push(this.challengeLiterals[colChallenge]());
      challenges = challenges.filter((cha) => cha != colChallenge);
    }
    return selectedChallenges;
  }
}

export default VidyaGridGame;
